import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { ReactComponent as PersonWalkingIcon } from '../../../SVGAssets/person-walking.svg';
import useMapStateStore from '../../../stores/mapStateStore';
import FavoriteButton from '../../Personalization/Favorite/FavoriteButton';
import FavoriteButtonsContext from '../../Personalization/Favorite/FavoriteButtonsContext';
import { CONTINUE_TOUR_BUTTON_TEXT } from '../utils/helpers';
import tgai from '../TourGuideAi.module.scss';

const PRIMARY_ACTIONS = ['next_step', 'collect_interests', 'restart_conversation', 'continue'];

const MINOR_ACTIONS = ['flush_deferred', 'end_exploration', 'restart_session'];

// Prospects never found the bare heart (#1619), so it carries a tooltip and an
// aria-label that names the amenity for screen readers.
export const FAVORITE_TITLE = 'Click to favorite';
export const FAVORITE_LABEL = 'Save to brochure';
export const FAVORITED_LABEL = 'Saved';

const Buttons = ({
  buttons,
  continueButton,
  links,
  conversationState,
  onButtonClick,
  isLatest,
  amenityPlaces,
  isSessionActive,
  onFavoriteToggle,
}) => {
  const {
    selectedInterests, selectedAmenities, visitedAmenities, visitedExternalLocations,
    selectedFollowUpAction,
  } = conversationState;
  const amenityModalOpen = useMapStateStore((s) => s.amenityModalOpen);
  // Read the context directly rather than via useFavoriteButtons, which throws
  // outside a provider; without one the label just stays in its unsaved form.
  const isFavorited = useContext(FavoriteButtonsContext)?.isFavorited;

  const allButtons = [...buttons,
    ...(continueButton ? [{ ...continueButton, action: continueButton.action || 'continue' }] : []),
    ...links];

  if (allButtons.length === 0) return null;

  const allInterestButtons = buttons.length > 0 && buttons.every(
    (button) => button.action === 'toggle_interest',
  );

  return (
    <div
      className={cx(tgai.stack, tgai.s, {
        [tgai.twoCol]: allInterestButtons,
      })}
    >
      {allButtons.map((button) => {
        const isPrimary = PRIMARY_ACTIONS.includes(button.action);
        // Minor only beside something else; on its own it stays a full button.
        const isMinor = MINOR_ACTIONS.includes(button.action) && allButtons.length > 1;
        let isSelected = false;
        let isDisabled = false;

        if (!isLatest && button.action !== 'view_amenity' && button.action !== 'toggle_amenity' && button.action !== 'see_external_locations') {
          isDisabled = true;
        }

        if (button.action === 'toggle_interest') {
          isSelected = selectedInterests.some((i) => i.uuid === button.interest);
          if (!isDisabled) {
            isDisabled = selectedInterests.length >= 3 && !isSelected;
          }
        }

        if (button.action === 'toggle_amenity' || button.action === 'view_amenity') {
          isSelected = selectedAmenities?.includes(button.amenity);
        }

        if (
          button.action === 'ask_question'
          || button.action === 'continue_tour'
          || button.action === 'decline_more_places'
        ) {
          isSelected = selectedFollowUpAction === button.action;
          if (!isLatest && isSelected) {
            isDisabled = true;
          }
        }

        const isVisitedAmenity = (button.action === 'toggle_amenity' || button.action === 'view_amenity')
          && visitedAmenities?.includes(button.amenity)
          && !amenityModalOpen;

        const isVisitedExternal = button.action === 'see_external_locations'
          && visitedExternalLocations?.includes(button.text || 'external');

        const isVisited = isVisitedAmenity || isVisitedExternal;

        const buttonKey = button.interest || button.amenity || button.action || button.text;

        const buttonText = button.text === CONTINUE_TOUR_BUTTON_TEXT && !isMinor
          ? 'Continue Tour'
          : button.text;

        const buttonClass = cx({
          [tgai.btn]: !isMinor,
          [tgai.fit]: !isMinor,
          [tgai.selected]: isSelected,
          [tgai.textBtn]: isMinor,
          [tgai.primary]: isPrimary && !isMinor,
          [tgai.secondary]: !isPrimary && !isMinor,
          [tgai.big]: button.interest,
          [tgai.visited]: isVisited,
        });

        const label = isMinor ? buttonText : (
          <span
            className={tgai.fitLabel}
            style={{ '--chars': Math.max(buttonText?.length || 1, 1) }}
          >
            {buttonText}
          </span>
        );

        // The brochure opens in a new tab, so it is a link styled as a button.
        if (button.href) {
          return (
            <a
              key={buttonKey}
              className={buttonClass}
              href={button.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`tgai-button:${button.action}`}
              data-tgai-button=""
              data-action={button.action}
            >
              {label}
            </a>
          );
        }

        const amenityButton = (
          <button
            type="button"
            key={buttonKey}
            className={buttonClass}
            onClick={() => onButtonClick(button)}
            disabled={isDisabled}
            data-testid={`tgai-button:${button.interest || button.amenity || button.action || buttonKey}`}
            data-tgai-button=""
            data-action={button.action}
            data-interest={button.interest}
            data-amenity={button.amenity}
            data-selected={String(isSelected)}
          >
            {isVisited && <PersonWalkingIcon className={tgai.visitedIcon} />}
            {label}
          </button>
        );

        // Amenity buttons get a favorite heart (place-typed, keyed by uuid) to
        // match the Interactive Map. The button only carries the node id, so
        // resolve the full place for its uuid; FavoriteButton self-gates on
        // personalizationActive and syncs field_favorite_amenities to Drupal.
        const favoritePlace = button.action === 'view_amenity'
          ? amenityPlaces.find((place) => String(place.id) === String(button.amenity))
          : null;

        if (favoritePlace?.uuid) {
          const favoriteLabel = isFavorited?.('place', favoritePlace.uuid)
            ? FAVORITED_LABEL
            : FAVORITE_LABEL;
          return (
            <div key={buttonKey} className={tgai.amenityRow}>
              {amenityButton}
              <span
                className={tgai.amenityFavorite}
                data-testid={`tgai-favorite:${button.amenity}`}
              >
                <FavoriteButton
                  type="place"
                  uuid={favoritePlace.uuid}
                  forceVisible={isSessionActive}
                  ariaLabel={`${favoriteLabel}: ${favoritePlace.title || buttonText}`}
                  title={FAVORITE_TITLE}
                  onToggle={(nowFavorited) => onFavoriteToggle
                    && onFavoriteToggle(favoritePlace, nowFavorited)}
                />
              </span>
            </div>
          );
        }

        return amenityButton;
      })}
    </div>
  );
};

Buttons.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.string,
    interest: PropTypes.string,
    amenity: PropTypes.string,
    href: PropTypes.string,
  })),
  continueButton: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.string,
  }),
  links: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.string,
  })),
  conversationState: PropTypes.shape({
    selectedInterests: PropTypes.arrayOf(PropTypes.shape({
      uuid: PropTypes.string,
      label: PropTypes.string,
    })),
    selectedAmenities: PropTypes.arrayOf(PropTypes.string),
    visitedAmenities: PropTypes.arrayOf(PropTypes.string),
    visitedExternalLocations: PropTypes.arrayOf(PropTypes.string),
    selectedFollowUpAction: PropTypes.string,
  }).isRequired,
  onButtonClick: PropTypes.func.isRequired,
  isLatest: PropTypes.bool,
  amenityPlaces: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    uuid: PropTypes.string,
  })),
  isSessionActive: PropTypes.bool,
  onFavoriteToggle: PropTypes.func,
};

Buttons.defaultProps = {
  buttons: [],
  continueButton: null,
  links: [],
  isLatest: true,
  amenityPlaces: [],
  isSessionActive: false,
  onFavoriteToggle: null,
};

export default Buttons;
